/**
 * Dropdown-related field renderers for DeviceModal.
 *
 * Exports `renderField` which dispatches to dropdown/input helpers.
 */
import { FusionInputField, FusionDropDownInput } from 'components';

/**
 * Render a field definition using component state and helpers.
 * Supports `valid-device` and `input-field` types.
 *
 * @param {object} formData
 * @param {function} setFormData
 * @param {object} field
 * @param {Array<object>} deviceTypes
 * @param {object|null} device
 * @returns {JSX.Element|null}
 */
export const renderField = (formData, setFormData, field, deviceTypes, device) => {
  switch (field.type) {
    case 'valid-device':
      return renderValidDevice(formData, setFormData, field, deviceTypes, device);
    case 'input-field':
      return renderInputField(formData, setFormData, field);
    default:
      return null;
  }
};

/**
 * Render a simple input field using `FusionInputField`.
 *
 * @param {object} formData
 * @param {function} setFormData
 * @param {object} field
 * @returns {JSX.Element}
 */
const renderInputField = (formData, setFormData, field) => {
  return (
    <>
      <div>{field.label}</div>
      <FusionInputField
        value={formData[field['data-field']] || ''}
        onChange={(value) => setFormData({ ...formData, [field['data-field']]: value })}
      />
    </>
  );
};

/**
 * Render a valid-device dropdown populated from `deviceTypes`.
 *
 * When `device` is provided (editing an existing device) the control
 * is not rendered.
 *
 * @param {object} formData
 * @param {function} setFormData
 * @param {object} field
 * @param {Array<object>} deviceTypes
 * @param {object|null} device
 * @returns {JSX.Element|null}
 */
const renderValidDevice = (formData, setFormData, field, deviceTypes, device) => {
  if (device) return null;

  const systemEntry = deviceTypes[parseInt(formData.system_type)];
  const systemType = systemEntry?.system_type;
  const isUSGS = systemType === 'USGS';

  const validDevices =
    systemEntry?.device_types[parseInt(formData.device_type)]?.validDevices;

  const selectedDevice =
    parseInt(formData[field['data-field']]) >= 0
      ? validDevices?.[parseInt(formData[field['data-field']])]
      : null;

  return (
    <>
      <FusionDropDownInput
        label={field.label}
        options={validDevices?.map((device) => `${device?.node_id} ${device?.name}`)}
        value={formData[field['data-field']] || -1}
        onChange={(value) => {
          const pickedDevice = parseInt(value) >= 0 ? validDevices[parseInt(value)] : null;
          // Auto-fill alias with Station Name only when alias is currently empty
          const aliasUpdate =
            isUSGS && pickedDevice?.name && !formData.alias?.trim()
              ? { alias: pickedDevice.name }
              : {};
          setFormData({
            ...formData,
            [field['data-field']]: value,
            device: pickedDevice,
            ...aliasUpdate,
          });
        }}
      />
      {/* Read-only Station Name displayed after a USGS station is selected */}
      {isUSGS && selectedDevice?.name && (
        <div className="mt-1">
          <div className="text-muted small mb-1">Station Name</div>
          <div
            className="form-control bg-secondary text-white border-0"
            style={{ cursor: 'default', userSelect: 'text' }}
            aria-readonly="true"
          >
            {selectedDevice.name}
          </div>
        </div>
      )}
    </>
  );
};
